import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Box,            
  Typography,     
  Divider         
} from "@mui/material";
import MDButton from "components/MDButton";
import { LanguageContext } from "../../../../context/LanguageContext";
import axiosInstance from "api/axiosInstance";

export default function StrategyGroupDialog({ open, onClose, onSelect, orderTypeFilter, demoMode = false, localGroups = null }) {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sortOption, setSortOption] = useState("default");

  useEffect(() => {
      loadLanguageFile("StrategyGroupDialog", "strategy/StrategyGroupDialog/StrategyGroupDialog_lang");
    }, [language, loadLanguageFile]);
  
    const LANGUAGE = componentLanguageData.StrategyGroupDialog || { Main: {} };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    // DEMO 頁：公開、打不到需登入的 API → 用本地策略組(依語系篩選)，補 id/等級對齊正式版形狀，
    // 並把 entry/exit 一起帶著(選取後直接插入，不再打 /strategy/strategy-group/{id})
    if (demoMode) {
      const lang = (language || "").toUpperCase();
      const norm = lang.includes("EN") ? "EN" : lang.includes("CN") ? "CN" : "ZH";
      const mapAll = (arr) => (arr || []).map((g, i) => ({
        id: `demo-${i}`, name: g.name, rewardLevel: "MEDIUM", riskLevel: "MEDIUM",
        entry: g.entry, exit: g.exit, filter: g.filter,
      }));
      const byLang = (localGroups || []).filter((g) => g.language === norm);
      setGroups(mapAll(byLang.length ? byLang : (localGroups || []).filter((g) => g.language === "EN")));
      setLoading(false);
      return;
    }
    try {
      const res = await axiosInstance.get("/strategy/strategy-groups", {
        headers: { "Accept-Language": language },
        params: { type: orderTypeFilter } // ✅ 傳遞下單類型過濾條件 (ASK/BID)
      });
      setGroups(res.data);
    } catch (err) {
      console.error("❌ 無法取得策略組清單:", err);
    } finally {
      setLoading(false);
    }
  }, [language, orderTypeFilter, demoMode, localGroups]);

  useEffect(() => {
    if (open) fetchGroups();
  }, [open, language, orderTypeFilter, fetchGroups]); // 加入 orderTypeFilter 依賴

  // 等級對應字串
  const levelMap = {
    HIGH: LANGUAGE.Main.LevelHigh || "高",
    MEDIUM: LANGUAGE.Main.LevelMedium || "中",
    LOW: LANGUAGE.Main.LevelLow || "低",
  };

  // 顏色對應
  const levelColor = (level) => {
    switch (level) {
      case "HIGH":
        return "error"; // 紅
      case "MEDIUM":
        return "warning"; // 黃
      case "LOW":
        return "success"; // 綠
      default:
        return "default";
    }
  };

  // 排序函式
  const sortGroups = (groups, option) => {
    const order = { HIGH: 3, MEDIUM: 2, LOW: 1 };
    const copy = [...groups];
    switch (option) {
      case "reward_desc":
        return copy.sort((a, b) => order[b.rewardLevel] - order[a.rewardLevel]);
      case "risk_asc":
        return copy.sort((a, b) => order[a.riskLevel] - order[b.riskLevel]);
      default:
        return copy.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  const sortedGroups = sortGroups(groups, sortOption);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{LANGUAGE.Main.Content1}</DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
  {loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      {/* 🔹 排序列（美化版） */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 1.5,
          px: 0.5,
          py: 0.5,
          backgroundColor: "rgba(0,0,0,0.03)",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ ml: 1, fontWeight: 600 }}
        >
          {LANGUAGE.Main.Content2}
        </Typography>

        <FormControl
          size="small"
          sx={{
            minWidth: 180,
            mr: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "white",
            },
          }}
        >
          <Select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <MenuItem value="default">{LANGUAGE.Main.Content3}</MenuItem>
            <MenuItem value="reward_desc">{LANGUAGE.Main.Content4}</MenuItem>
            <MenuItem value="risk_asc">{LANGUAGE.Main.Content5}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {/* 🔹 策略組清單 */}
      <List sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
        {sortedGroups.map((g) => (
          <ListItemButton
            key={g.id}
            selected={selected?.id === g.id}
            onClick={() => setSelected(g)}
            sx={{
              borderRadius: 2,
              mb: 1,
              border:
                selected?.id === g.id
                  ? "2px solid #1976d2"
                  : "1px solid #e0e0e0",
              boxShadow:
                selected?.id === g.id
                  ? "0px 0px 6px rgba(25,118,210,0.3)"
                  : "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(25,118,210,0.04)",
              },
            }}
          >
            <ListItemText
              primary={
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {g.name}
                  </Typography>
                  <Chip
                    label={`${LANGUAGE.Main.Content6}${levelMap[g.rewardLevel]}`}
                    color={levelColor(g.rewardLevel)}
                    size="small"
                  />
                  <Chip
                    label={`${LANGUAGE.Main.Content7}${levelMap[g.riskLevel]}`}
                    color={levelColor(g.riskLevel)}
                    size="small"
                  />
                </Stack>
              }
            />
          </ListItemButton>
        ))}
      </List>
    </>
  )}
</DialogContent>

      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          {LANGUAGE.Main.Content8}
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!selected}
          onClick={() => {
            onSelect(selected);
            onClose();
          }}
        >
          {LANGUAGE.Main.Content9}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
